// src/components/dashboard/Workshop1Dashboard.js
// Professional Workshop 1 Dashboard using Tremor layout + ECharts
import React from 'react';
import {
  Card,
  Title,
  Text
} from '@tremor/react';
import { useTranslation } from 'react-i18next';
import EChart from './charts/EChart';
import DashboardGrid from './DashboardGrid';
import { W1_DEFAULT_LAYOUT } from './defaultLayouts';
import { SEVERITY_COLORS } from './charts/ebiosTheme';
import { SECURITY_PILLARS } from '../../constants';
import {
  SEVERITY_ORDER,
  normalizeSeverity,
  splitTimelineBuckets,
  computeMaturity,
  buildBusinessValuePillarMatrix
} from './charts/workshop1Data';

// Color mapping for status names (tremor names, kept for legend mapping)
const statusColors = {
  'En place': 'emerald',
  'Partiel': 'blue',
  'Planifié': 'amber',
  'À appliquer avant le': 'violet',
  'À abandonner': 'rose',
  'Implemented': 'emerald',
  'Partial': 'blue',
  'Planned': 'amber',
  'To Apply': 'violet',
  'To Abandon': 'rose'
};

const TREMOR_HEX = {
  emerald: '#10B981',
  blue: '#3B82F6',
  amber: '#F59E0B',
  violet: '#8B5CF6',
  rose: '#F43F5E',
  slate: '#64748B'
};

const Workshop1Dashboard = ({
  contextData = {},
  businessValues = [],
  dreadedEvents = [],
  securityFramework = {},
  securityControlStatusData = [],
  controlsData = [],
  participantRolesData = [],
  businessValuePillarsData = [],
  eventsBySeverityData = [],
  securityPillarEventsData = [],
  eventsByImpactData = [],
  editMode = false,
  layouts = null,
  onLayoutChange = null,
  onToggleHidden = null
}) => {
  const { t } = useTranslation();
  void securityFramework;
  void eventsBySeverityData;

  // Severity counts — single source: raw dreadedEvents via normalizeSeverity
  const severityCounts = dreadedEvents.reduce((acc, event) => {
    const sev = normalizeSeverity(event.severity || event.gravity);
    const existing = acc.find((item) => item.name === sev);
    if (existing) existing.value += 1;
    else acc.push({ name: sev, value: 1 });
    return acc;
  }, []).sort((a, b) => (SEVERITY_ORDER[b.name] || 0) - (SEVERITY_ORDER[a.name] || 0));
  const severityTotal = severityCounts.reduce((sum, item) => sum + item.value, 0);

  // Pillar data from prop (normalization done upstream in Dashboard)
  const pillarChartData = securityPillarEventsData.map((item) => ({
    name: item.subject || item.name,
    value: item.value
  })).filter((item) => item.value > 0);
  const sortedPillars = [...pillarChartData].sort((a, b) => b.value - a.value);

  // Maturity from raw controls (implementedPct feeds the status donut center)
  const maturity = computeMaturity(controlsData);
  const implementedPct = maturity.implementedPct;

  // Transform security status data for donut chart
  const statusChartData = securityControlStatusData.map((item) => ({
    name: item.name,
    value: item.value
  }));

  // Timeline buckets (dated vs estimated)
  const buckets = splitTimelineBuckets(controlsData, new Date());
  const shortLabel = `≤ 1 ${t('common.month')}`;
  const mediumLabel = `1-3 ${t('common.months')}`;
  const longLabel = `> 3 ${t('common.months')}`;
  const datedLabel = t('dashboard.workshop1.timelineDated');
  const estimatedLabel = t('dashboard.workshop1.timelineEstimated');

  const pillarOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
    xAxis: { type: 'value', minInterval: 1 },
    yAxis: {
      type: 'category',
      data: sortedPillars.map((d) => d.name),
      axisLabel: { interval: 0 }
    },
    series: [{
      type: 'bar',
      data: sortedPillars.map((d) => d.value),
      itemStyle: { color: '#8B5CF6', borderRadius: [0, 6, 6, 0] },
      ...(businessValues.length > 0 ? {
        markLine: {
          symbol: 'none',
          lineStyle: { color: '#10B981', type: 'dashed' },
          label: { formatter: t('dashboard.workshop1.fullCoverage'), color: '#059669' },
          data: [{ xAxis: businessValues.length }]
        }
      } : {})
    }]
  };

  const severityOption = {
    tooltip: { trigger: 'item' },
    title: {
      text: String(severityTotal),
      left: 'center',
      top: '38%',
      textStyle: { fontSize: 24, fontWeight: 'bold', color: '#111827' }
    },
    legend: {
      bottom: 0,
      data: severityCounts.map((d) => d.name)
    },
    series: [{
      type: 'pie',
      radius: ['50%', '70%'],
      center: ['50%', '45%'],
      label: { show: false },
      data: severityCounts.map((d) => ({
        name: d.name,
        value: d.value,
        itemStyle: { color: SEVERITY_COLORS[d.name] || '#94A3B8' }
      }))
    }]
  };

  const statusOption = {
    tooltip: { trigger: 'item' },
    title: {
      text: `${implementedPct}%`,
      left: 'center',
      top: '38%',
      textStyle: { fontSize: 24, fontWeight: 'bold', color: '#111827' }
    },
    legend: {
      bottom: 0,
      data: statusChartData.map((d) => d.name)
    },
    series: [{
      type: 'pie',
      radius: ['50%', '70%'],
      center: ['50%', '45%'],
      label: { show: false },
      data: statusChartData.map((d) => ({
        name: d.name,
        value: d.value,
        itemStyle: { color: TREMOR_HEX[statusColors[d.name]] || '#64748B' }
      }))
    }]
  };

  const timelineOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { bottom: 0, data: [datedLabel, estimatedLabel] },
    grid: { left: 8, right: 16, top: 24, bottom: 48, containLabel: true },
    xAxis: { type: 'category', data: [shortLabel, mediumLabel, longLabel] },
    yAxis: { type: 'value', minInterval: 1 },
    series: [
      {
        name: datedLabel,
        type: 'bar',
        stack: 'total',
        data: [buckets.shortTerm.dated, buckets.mediumTerm.dated, buckets.longTerm.dated],
        itemStyle: { color: '#6366F1' }
      },
      {
        name: estimatedLabel,
        type: 'bar',
        stack: 'total',
        data: [buckets.shortTerm.estimated, buckets.mediumTerm.estimated, buckets.longTerm.estimated],
        itemStyle: { color: '#C7D2FE', borderRadius: [6, 6, 0, 0] }
      }
    ]
  };

  const bvPillarMatrix = buildBusinessValuePillarMatrix(businessValues, dreadedEvents, SECURITY_PILLARS);
  const showBvPillarHeatmap = businessValues.length > 0 && dreadedEvents.length > 0;

  const bvPillarHeatmapOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const bvName = bvPillarMatrix.rowLabels[params.value[1]] || '';
        const pillarName = bvPillarMatrix.columnLabels[params.value[0]] || '';
        return `${bvName} • ${pillarName}: ${params.value[2]}`;
      }
    },
    grid: { left: 8, right: 16, top: 8, bottom: 64, containLabel: true },
    xAxis: {
      type: 'category',
      data: bvPillarMatrix.columnLabels,
      axisLabel: { interval: 0, rotate: 30 }
    },
    yAxis: {
      type: 'category',
      data: bvPillarMatrix.rowLabels,
      axisLabel: { interval: 0 }
    },
    visualMap: {
      min: 0,
      max: Math.max(bvPillarMatrix.max, 1),
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      inRange: { color: ['#E0E7FF', '#4F46E5'] }
    },
    series: [{
      type: 'heatmap',
      data: bvPillarMatrix.data,
      label: { show: true },
      emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0, 0, 0, 0.3)' } }
    }]
  };

  const impactOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
    xAxis: { type: 'value', minInterval: 1 },
    yAxis: {
      type: 'category',
      data: eventsByImpactData.map((d) => d.name),
      axisLabel: { interval: 0 }
    },
    series: [{
      type: 'bar',
      data: eventsByImpactData.map((d) => ({
        value: d.value,
        itemStyle: { color: d.color || '#6366F1' }
      })),
      itemStyle: { borderRadius: [0, 6, 6, 0] }
    }]
  };

  return (
    // Force light theme wrapper
    <div className="space-y-6 bg-gray-50 p-4 rounded-xl">
      {/* Charts Grid (draggable/resizable in edit mode) */}
      <DashboardGrid
        items={[
          {
            id: 'w1-pillarBars',
            fullscreenTitle: t('dashboard.workshop2.eventsByPillar'),
            fullscreenContent: (<EChart option={pillarOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop2.eventsByPillar')}</Title>
                {sortedPillars.length > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: 90 }}><EChart option={pillarOption} height="100%" /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dreadedEvents.empty.noEvents')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          {
            id: 'w1-severityDonut',
            fullscreenTitle: t('dashboard.workshop2.eventsBySeverity'),
            fullscreenContent: (<EChart option={severityOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop2.eventsBySeverity')}</Title>
                {severityCounts.length > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: 90 }}><EChart option={severityOption} height="100%" /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dreadedEvents.empty.noEvents')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          {
            id: 'w1-statusDonut',
            fullscreenTitle: t('dashboard.charts.securityDistribution'),
            fullscreenContent: (<EChart option={statusOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.charts.securityDistribution')}</Title>
                {statusChartData.length > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: 90 }}><EChart option={statusOption} height="100%" /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dashboard.empty.noSecurityMeasures')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          ...(showBvPillarHeatmap ? [{
            id: 'w1-bvPillarHeatmap',
            fullscreenTitle: t('dashboard.workshop1.bvPillarHeatmap'),
            fullscreenContent: (<EChart option={bvPillarHeatmapOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop1.bvPillarHeatmap')}</Title>
                <div className="flex-1 min-h-0" style={{ minHeight: Math.max(100, bvPillarMatrix.rowLabels.length * 20 + 40) }}><EChart option={bvPillarHeatmapOption} height="100%" /></div>
              </Card>
            )
          }] : []),
          ...(controlsData.length > 0 ? [{
            id: 'w1-timeline',
            fullscreenTitle: t('dashboard.workshop1.implementationTimeline'),
            fullscreenContent: (<EChart option={timelineOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop1.implementationTimeline')}</Title>
                <div className="flex-1 min-h-0" style={{ minHeight: 90 }}><EChart option={timelineOption} height="100%" /></div>
              </Card>
            )
          }] : []),
          ...(eventsByImpactData.length > 0 ? [{
            id: 'w1-impactBars',
            fullscreenTitle: t('dashboard.workshop1.eventsByImpact'),
            fullscreenContent: (<EChart option={impactOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop1.eventsByImpact')}</Title>
                <div className="flex-1 min-h-0" style={{ minHeight: 90 }}><EChart option={impactOption} height="100%" /></div>
              </Card>
            )
          }] : [])
        ]}
        defaultLayout={W1_DEFAULT_LAYOUT}
        editMode={editMode}
        layouts={layouts}
        onLayoutChange={onLayoutChange}
        onToggleHidden={onToggleHidden}
      />
    </div>
  );
};

export default Workshop1Dashboard;
